import React, { useState, useRef, useEffect } from 'react';

interface ColorPickerProps {
    color: string;
    onChange: (color: string) => void;
    className?: string;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, className = '' }) => {
    const [hue, setHue] = useState(0);
    const [saturation, setSaturation] = useState(0);
    const [lightness, setLightness] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);

    // Parse HSL color
    useEffect(() => {
        if (color && color.includes('hsl')) {
            const match = color.match(/hsl\((\d+)\s+(\d+)%\s+(\d+)%\)/);
            if (match) {
                setHue(parseInt(match[1]));
                setSaturation(parseInt(match[2]));
                setLightness(parseInt(match[3]));
            }
        }
    }, [color]);

    // Convert HSL to hex for display
    const hslToHex = (h: number, s: number, l: number) => {
        l /= 100;
        const a = s * Math.min(l, 1 - l) / 100;
        const f = (n: number) => {
            const k = (n + h / 30) % 12;
            const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
            return Math.round(255 * color).toString(16).padStart(2, '0');
        };
        return `#${f(0)}${f(8)}${f(4)}`;
    };

    const handleColorChange = (newHue: number, newSaturation: number, newLightness: number) => {
        setHue(newHue);
        setSaturation(newSaturation);
        setLightness(newLightness);
        onChange(`hsl(${newHue} ${newSaturation}% ${newLightness}%)`);
    };

    const handleSaturationLightnessChange = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!pickerRef.current) return;

        const rect = pickerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const newSaturation = Math.round((x / rect.width) * 100);
        const newLightness = Math.round(100 - (y / rect.height) * 100);

        handleColorChange(hue, Math.max(0, Math.min(100, newSaturation)), Math.max(0, Math.min(100, newLightness)));
    };

    const handleHueChange = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const newHue = Math.round((x / rect.width) * 360);
        handleColorChange(Math.max(0, Math.min(360, newHue)), saturation, lightness);
    };

    const currentColor = `hsl(${hue} ${saturation}% ${lightness}%)`;

    return (
        <div className={`relative ${className}`}>
            <div
                className="w-12 h-12 rounded border-2 border-gray-300 cursor-pointer"
                style={{ backgroundColor: currentColor }}
                onClick={() => setIsOpen(!isOpen)}
            />

            {isOpen && (
                <div className="absolute top-14 left-0 z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                    <div className="space-y-4">
                        {/* Saturation/Lightness picker */}
                        <div className="relative">
                            <div
                                ref={pickerRef}
                                className="w-48 h-32 rounded border border-gray-300 cursor-crosshair relative"
                                style={{
                                    background: `linear-gradient(to right, 
                    hsl(${hue} 0% 50%), 
                    hsl(${hue} 100% 50%))`
                                }}
                                onClick={handleSaturationLightnessChange}
                            >
                                <div
                                    className="absolute w-3 h-3 border-2 border-white rounded-full shadow-md transform -translate-x-1/2 -translate-y-1/2"
                                    style={{
                                        left: `${saturation}%`,
                                        top: `${100 - lightness}%`,
                                        backgroundColor: currentColor
                                    }}
                                />
                            </div>
                        </div>

                        {/* Hue picker */}
                        <div className="relative">
                            <div
                                className="w-48 h-6 rounded border border-gray-300 cursor-pointer"
                                style={{
                                    background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
                                }}
                                onClick={handleHueChange}
                            >
                                <div
                                    className="absolute w-3 h-6 border-2 border-white rounded shadow-md transform -translate-x-1/2"
                                    style={{
                                        left: `${(hue / 360) * 100}%`,
                                        backgroundColor: `hsl(${hue}, 100%, 50%)`
                                    }}
                                />
                            </div>
                        </div>

                        {/* HSL inputs */}
                        <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">H</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="360"
                                    value={hue}
                                    onChange={(e) => handleColorChange(parseInt(e.target.value) || 0, saturation, lightness)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">S</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={saturation}
                                    onChange={(e) => handleColorChange(hue, parseInt(e.target.value) || 0, lightness)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">L</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={lightness}
                                    onChange={(e) => handleColorChange(hue, saturation, parseInt(e.target.value) || 0)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                />
                            </div>
                        </div>

                        {/* Hex input */}
                        <div>
                            <label className="block text-xs text-gray-600 mb-1">Hex</label>
                            <input
                                type="text"
                                value={hslToHex(hue, saturation, lightness)}
                                onChange={(e) => {
                                    const hex = e.target.value;
                                    if (/^#[0-9A-F]{6}$/i.test(hex)) {
                                        // Convert hex to HSL (simplified)
                                        const r = parseInt(hex.slice(1, 3), 16) / 255;
                                        const g = parseInt(hex.slice(3, 5), 16) / 255;
                                        const b = parseInt(hex.slice(5, 7), 16) / 255;

                                        const max = Math.max(r, g, b);
                                        const min = Math.min(r, g, b);
                                        let h = 0, s = 0, l = (max + min) / 2;

                                        if (max !== min) {
                                            const d = max - min;
                                            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                                            switch (max) {
                                                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                                                case g: h = (b - r) / d + 2; break;
                                                case b: h = (r - g) / d + 4; break;
                                            }
                                            h /= 6;
                                        }

                                        handleColorChange(Math.round(h * 360), Math.round(s * 100), Math.round(l * 100));
                                    }
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono"
                            />
                        </div>

                        {/* Close button */}
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsOpen(false)}
                                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};





























